import { isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { ReportingAccessLevels } from '../../../NonReact/constants/Permissions'

/**
 * Ordered "Licensed for" values shown in the partition modal for all TelSys layouts.
 * Aligned with {@link ReportingAccessLevels} API strings.
 */
export const PARTITION_LICENSED_FOR_VALUES: readonly ReportingAccessLevels[] = [
    ReportingAccessLevels.LITE,
    ReportingAccessLevels.ONE_THOUSAND,
    ReportingAccessLevels.TWO_THOUSAND,
    ReportingAccessLevels.ENTERPRISE
]

/**
 * Builds the ordered list of API values for the partition "Licensed for" select, including the current value when it
 * is outside the standard four options (legacy or unexpected API data).
 *
 * @param currentLicensedFor - Value currently selected in the form.
 * @param isEditMode - Whether the modal is editing an existing partition.
 * @param allOptionsRevealedForEnterprise - When {@code false} and the current value is Enterprise, only Enterprise is
 *     returned so other tiers are hidden until the user uses the reveal shortcut. In edit mode, when the current value
 *     is not Enterprise, the Enterprise option is also hidden until the same shortcut is used.
 * @returns Values to render as select options, in display order.
 */
export const buildPartitionLicensedForSelectValues = (
    currentLicensedFor: string,
    isEditMode: boolean,
    allOptionsRevealedForEnterprise: boolean
): string[] => {
    const all: readonly string[] = PARTITION_LICENSED_FOR_VALUES
    let ordered: string[]

    if (currentLicensedFor === ReportingAccessLevels.ENTERPRISE && !allOptionsRevealedForEnterprise) {
        ordered = [ReportingAccessLevels.ENTERPRISE]
    } else if (isEditMode && !allOptionsRevealedForEnterprise) {
        ordered = all.filter((value: string): boolean => value !== ReportingAccessLevels.ENTERPRISE)
    } else {
        ordered = [...all]
    }

    if (isStringNotEmpty(currentLicensedFor) && !ordered.includes(currentLicensedFor)) {
        ordered.push(currentLicensedFor)
    }

    return ordered
}

/**
 * Determines whether the partition "Licensed for" field should be locked.
 *
 * @param fieldDisabled - Whether the field is already disabled for another reason such as view-only mode.
 * @param currentLicensedFor - Value currently selected in the form.
 * @param allOptionsRevealedForEnterprise - Whether the Enterprise shortcut has unlocked the field.
 * @returns True when the select should be disabled.
 */
export const shouldDisablePartitionLicensedForField = (
    fieldDisabled: boolean,
    currentLicensedFor: string,
    allOptionsRevealedForEnterprise: boolean
): boolean =>
    fieldDisabled || (currentLicensedFor === ReportingAccessLevels.ENTERPRISE && !allOptionsRevealedForEnterprise)

/**
 * @returns The fixed partition "Licensed for" option values (same for all synchronisation layouts).
 */
export const getPartitionLicensedForOptionValues = (): readonly ReportingAccessLevels[] => PARTITION_LICENSED_FOR_VALUES
